import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWatchlist } from './context/WatchlistContext';
import { useModal } from './context/ModalContext';
import { ApiService } from './services/api';
import { ManhwaRecap } from './types';
import { RecapCard } from './components/RecapCard';
import { RecapModal } from './components/RecapModal';
import { LoadingSpinner } from './components/LoadingSpinner';
import { BookOpen, Home } from 'lucide-react'; // Added Home icon
import { Navigation } from './components/Navigation';
import { ThemeToggle } from './components/ThemeToggle';

const WatchlistPage: React.FC = () => {
  const { watchlist, loading: watchlistLoading } = useWatchlist();
  const { selectedRecap, isModalOpen, openModal, closeModal } = useModal();
  const [watchedRecaps, setWatchedRecaps] = useState<ManhwaRecap[]>([]);
  const [loadingRecaps, setLoadingRecaps] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (watchlistLoading) {
      // Wait for the initial watchlist IDs to be loaded
      return;
    }

    const fetchWatchlistRecaps = async () => {
      if (watchlist.length === 0) {
        setWatchedRecaps([]);
        setLoadingRecaps(false);
        return;
      }

      setLoadingRecaps(true);
      setError(null);
      try {
        const recaps = await ApiService.getRecapsByIds(watchlist.map(id => id.toString()));
        setWatchedRecaps(recaps);
      } catch (err) {
        console.error('Error fetching watchlist recaps:', err);
        setError('Failed to load watchlist recaps. Please try again later.');
      }
      setLoadingRecaps(false);
    };

    fetchWatchlistRecaps();
  }, [watchlist, watchlistLoading]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Header */}
      <header className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4 lg:gap-6">
              <Link to="/" className="flex items-center gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-purple-600 rounded-lg">
                  <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">ManhwaRecap</h1>
                </div>
              </Link>
              <Navigation />
            </div>
            <div className="flex items-center space-x-1 md:space-x-2 lg:space-x-4">
              <Link to="/" className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors">
                <Home className="w-4 h-4" />
                Back to Home
              </Link>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">My Watchlist</h1>
          <p className="text-gray-600 dark:text-gray-400">Your saved recaps</p>
        </div>

        {(watchlistLoading || loadingRecaps) && <div className="flex justify-center items-center h-64"><LoadingSpinner size={48} /></div>}
        {error && <p className="text-center text-red-500">{error}</p>}
        {!watchlistLoading && !loadingRecaps && !error && watchedRecaps.length === 0 && (
          <div className="text-center py-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Your Watchlist is Empty</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Start adding recaps to your watchlist to see them here.</p>
            <Link
              to="/"
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 inline-flex items-center gap-2"
            >
              <Home className="w-5 h-5" /> Find Recaps
            </Link>
          </div>
        )}
        {!watchlistLoading && !loadingRecaps && !error && watchedRecaps.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {watchedRecaps.map(recap => (
              <RecapCard
                key={recap.id}
                recap={recap}
                onClick={() => openModal(recap)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Modal */}
      <RecapModal
        recap={selectedRecap}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default WatchlistPage;
